import React, { useEffect, useState } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { Link } from "react-router-dom";
import { Select } from "antd";
import { toast } from "sonner";
import { getAllClass } from "../../api/pedagogicElement";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { getAllEvaluationPeriodByLevel } from "../../api/generalParam";
import { getAllUEByPeriod } from "../../api/ue";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { generateNoteFiche } from "../../api/notes";

const FicheNote = () => {
  // 👉 Etats pour la sélection de l'année académique en cours
  const { selectedYear, setSelectedYear } = useAcademicYear();

  // 👉 Etats pour la sélection des classes et périodes
  const [allClasses, setallClasses] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState<any>(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const [iframeVisible, setIframeVisible] = useState(false);

  const [isFileLoading, setIsFileLoading] = useState(false); // Pour la génération de la fiche

  // 👉 Etats pour la sélection des période d'evaluation et sa liste
  const [allEvaluationPeriodList, setAllEvaluationPeriodList] = useState<any[]>(
    [],
  );
  const [selectedEvaluationPeriod, setSelectedEvaluationPeriod] =
    useState<any>(null);
  const [ueList, setUeList] = useState<any[]>([]);
  const [selectedECU, setSelectedECU] = useState<any>(null);

  // 👉 Etats pour le chargement des classes, périodes et UE
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingPeriods, setIsLoadingPeriods] = useState(false);
  const [isLoadingUEs, setIsLoadingUEs] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // state pour le PDF généré
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  // 👉 Change handler pour le select des classes
  const handleClassChange = (selectedId: string) => {
    const selected = allClasses.find(
      (c: any) => c.id.toString() === selectedId,
    );
    if (selected) setSelectedClass(selected);
  };

  // 👉 Change handler pour le select des périodes
  const handlePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const selected = allEvaluationPeriodList.find(
      (c: any) => c.id.toString() === selectedId,
    );
    if (selected) setSelectedEvaluationPeriod(selected);
  };

  // 👉 Change handler pour le select des Ecu
  const handleEcuChange = (selectedId: string) => {
    if (!selectedId) {
      setSelectedECU(null);
      return;
    }

    for (const ue of ueList) {
      const foundSubject = ue.SubjectUEs?.find(
        (subject: any) => subject.id.toString() === selectedId,
      );

      if (foundSubject) {
        setSelectedECU({
          ...foundSubject,
          parentUE: {
            id: ue.id,
            label: ue.label,
            codeUE: ue.codeUE,
            type: ue.type,
          },
        });
        return;
      }
    }
  };

  // Fontion pour générer une fiche de note selon la périod , année academique , la classe et l'étudiant
  const generateFicheNote = async () => {
    try {
      const response = await generateNoteFiche({
        id_subjectUE: selectedECU.id,
      });
      // console.log("releve generation : ", response);
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setIframeVisible(true);
      // console.log("relevé généré: ", response);
      toast.success("Fiche de note généré avec succès");
    } catch (error: any) {
      // console.log("erreur génération fiche de note: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsLoading(false); // ← Toujours arrêter le chargement à la fin
    }
  };

  /**
   * 👉 Chargement des classes disponibles pour l'année académique sélectionnée
   * - Dépend de selectedYear
   * - Charge toutes les classes et sélectionne automatiquement la première si dispo
   */
  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;
    setIsLoadingClasses(true);
    setSelectedEvaluationPeriod(null);

    const fetchClasses = async () => {
      try {
        const response = await getAllClass(
          selectedYear?.id,
          buildPermissionContext(Resource.EMARGEMENT, Action.CREATE),
        );
        const classes = response.data || [];

        setallClasses(classes);

        // Sélectionne la première classe automatiquement s'il y en a
        if ((classes || []).length > 0) {
          setSelectedClass(classes[0]);
        }
      } catch (error: any) {
        // console.log("erreur de chargement des classes : ", error);
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Une erreur s'est produit, Réessayer plus tard",
        );

        // Option: reset les classes en cas d'erreur
        setallClasses([]);
      } finally {
        setIsLoadingClasses(false); // ← Toujours arrêter le chargement à la fin
      }
    };

    fetchClasses();
  }, [selectedYear]);

  /**
   * 👉 Chargement des périodes d'évaluation pour le niveau de la classe sélectionnée
   * - Dépend de selectedClass
   * - Charge toutes les périodes et sélectionne automatiquement la première si dispo
   */
  useEffect(() => {
    if (!selectedClass?.Level?.id) return;
    setIsLoadingPeriods(true);
    setSelectedECU(null);

    const fetchPeriods = async () => {
      try {
        const response = await getAllEvaluationPeriodByLevel(
          selectedClass?.Level?.id,
          buildPermissionContext(Resource.EMARGEMENT, Action.CREATE),
        );
        const periods = response.data || [];

        setAllEvaluationPeriodList(periods);

        // Sélectionne la première période automatiquement s'il y en a
        if ((periods || []).length > 0) {
          setSelectedEvaluationPeriod(periods[0]);
        }
      } catch (error: any) {
        // console.log("erreur de chargement des périodes : ", error);
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Une erreur s'est produit, Réessayer plus tard",
        );
        // Option: reset les periodes en cas d'erreur
        setAllEvaluationPeriodList([]);
      } finally {
        setIsLoadingPeriods(false); // ← Toujours arrêter le chargement à la fin
      }
    };

    fetchPeriods();
  }, [selectedClass]);

  /**
   * 👉 Chargement des Unités d'Enseignement (UE) pour la classe et la période sélectionnées
   * - Dépend de selectedClass, selectedEvaluationPeriod et refreshKey
   * - Charge toutes les UE et sélectionne automatiquement la première si dispo
   */
  useEffect(() => {
    if (!selectedClass?.id || !selectedEvaluationPeriod?.id) return;
    setIsLoadingUEs(true);
    setSelectedECU(null);

    const fetchECUs = async () => {
      try {
        const response = await getAllUEByPeriod(
          selectedClass.id,
          selectedEvaluationPeriod.id,
        );
        const ues = response || [];

        setUeList(ues.data || []);

        // Trouver la première UE qui contient des SubjectUEs
        const firstUEWithSubjects = ues.data.find(
          (ue: any) => ue.SubjectUEs?.length > 0,
        );

        if (firstUEWithSubjects) {
          // Prendre le premier SubjectUE de cette UE
          const firstSubject = firstUEWithSubjects.SubjectUEs[0];
          setSelectedECU({
            ...firstSubject,
            parentUE: {
              // Conserver les infos de l'UE parente
              id: firstUEWithSubjects.id,
              label: firstUEWithSubjects.label,
              codeUE: firstUEWithSubjects.codeUE,
              type: firstUEWithSubjects.type,
            },
          });
        }
      } catch (error: any) {
        // console.log("erreur de chargement des ECUs : ", error);
        toast.error(
          error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            "Une erreur s'est produit, Réessayer plus tard",
        );

        // Option: reset les UEs en cas d'erreur
        setUeList([]);
      } finally {
        setIsLoadingUEs(false); // ← Toujours arrêter le chargement à la fin
      }
    };

    fetchECUs();
  }, [refreshKey, selectedClass, selectedEvaluationPeriod]);

  return (
    <>
      {/* Fiche d'émargement */}
      <div
        className="chat chat-messages"
        style={{ border: "1px solid #B5B9C3" }}
        id="middle"
      >
        {/* Entête */}
        <div
          className="chat-header"
          style={{ borderBottom: "1px solid #B5B9C3" }}
        >
          <div className="user-details">
            <div className="bg-primary-light rounded-circle p-2 me-3">
              <i className="ion ion-clipboard fs-4" />
            </div>
            <div>
              <h4>Fiche de note</h4>
            </div>
          </div>
          <div className="chat-options">
            <ul className="list-inline">
              <li className="list-inline-item">
                <Link
                  className="btn btn-outline-light no-bg"
                  to="#"
                  data-bs-toggle="dropdown"
                >
                  <i className="bx bx-dots-vertical-rounded" />
                </Link>
                <div className="dropdown-menu dropdown-menu-end">
                  <Link to="#" className="dropdown-item">
                    <span>
                      <i className="bx bx-x" />
                    </span>
                    Close Chat
                  </Link>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Contenu remplacé par l'iframe */}
        {iframeVisible && pdfBlobUrl ? (
          <iframe
            src={pdfBlobUrl}
            style={{
              width: "100%",
              height: "calc(100vh - 80px)", // Ajustez selon la hauteur du header
              border: "none",
              marginTop: "10px",
            }}
            title="Aperçu PDF"
            onError={(e) => {
              // console.error("Erreur de chargement du PDF", e);
              // Gestion d'erreur optionnelle
            }}
          />
        ) : (
          <div
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ height: "calc(100vh - 80px)" }}
          >
            {/* Version sobre */}
            <div className="text-center p-4">
              <div className="bg-light rounded-circle p-4 mb-3 d-inline-flex">
                <i className="bx bx-file-blank fs-1 text-muted"></i>
              </div>
              <h4 className="text-muted mb-2">
                {pdfBlobUrl ? "Chargement du PDF..." : "Aucun PDF à afficher"}
              </h4>
              <p className="text-muted">
                {!pdfBlobUrl && "Générez un nouveau PDF pour l'afficher ici"}
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Fiche d'émargement */}
      {/* Sidebar Droit */}
      <div
        className="right-sidebar right_sidebar_profile right-side-contact show-right-sidebar"
        id="middle1"
      >
        <div className="right-sidebar-wrap active">
          <div className="slimscroll">
            <Scrollbars>
              <div className="sidebar-body">
                <div className="mt-0 right_sidebar_logo">
                  <div className="text-center right-sidebar-profile">
                    <div className="bg-primary-light rounded-circle p-2 me-3">
                      <i className="ti ti-clipboard-check fs-4" />
                    </div>
                    <h5 className="profile-name">Paramètres</h5>
                    <div className="last-seen-profile">
                      <span>Sélectionnez les options de la fiche</span>
                    </div>
                  </div>
                  <div className=" chat-member-details mb-2">
                    <label className="form-label">Classe</label>
                    <Select
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toString()
                          .toLowerCase()
                          .includes(input.toString().toLowerCase())
                      }
                      size="large"
                      style={{ width: "100%" }}
                      placeholder="Sélectionner la classe"
                      value={selectedClass?.id?.toString()}
                      onChange={handleClassChange}
                      options={allClasses
                        .slice()
                        .sort((a: any, b: any) => {
                          const levelCompare = a.Level.label.localeCompare(
                            b.Level.label,
                            "fr",
                            { numeric: true },
                          );
                          if (levelCompare !== 0) return levelCompare;
                          return a.label.localeCompare(b.label, "fr", {
                            numeric: true,
                          });
                        })
                        .map((classe: any) => ({
                          label: `${classe.Level.label} - ${classe.label}`,
                          value: classe.id.toString(),
                        }))}
                      className="mb-2"
                    />

                    <label className="form-label">Période d'évaluation</label>
                    <select
                      value={selectedEvaluationPeriod?.id.toString() || ""}
                      onChange={handlePeriodChange}
                      className="form-select align-items-center mb-2"
                      aria-label=".form-select-lg example"
                    >
                      <option value={""}>Sélectionner une période</option>
                      {allEvaluationPeriodList.map((period: any) => (
                        <option
                          key={period.id}
                          value={period.id.toString()}
                        >{`${period.label}`}</option>
                      ))}
                    </select>

                    <label className="form-label">Matière - ECU </label>
                    <Select
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toString()
                          .toLowerCase()
                          .includes(input.toString().toLowerCase())
                      }
                      size="large"
                      style={{ width: "100%" }}
                      placeholder="Sélectionner une matière"
                      value={selectedECU?.id?.toString()}
                      onChange={handleEcuChange}
                      options={ueList.flatMap(
                        (ue: any) =>
                          ue.SubjectUEs?.map((mat: any) => ({
                            label: mat.Subject.label,
                            value: mat.id.toString(),
                          })) || [],
                      )}
                      className="mb-2"
                    />
                  </div>
                </div>
              </div>
              <div className="right-sidebar-head share-media">
                <div className="d-grid gap-2 mb-4">
                  {isFileLoading ? (
                    <button
                      className="btn btn-primary-light"
                      type="button"
                      disabled={false}
                    >
                      <span
                        className="spinner-border spinner-border-sm align-middle me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Chargement...
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        if (
                          !selectedClass ||
                          !selectedEvaluationPeriod ||
                          !selectedECU
                        ) {
                          toast.error(
                            "Veuillez sélectionner une classe, une période et une matière",
                          );
                          return;
                        }

                        try {
                          setIsFileLoading(true);
                          await generateFicheNote();
                        } catch (error) {
                          // console.error("Erreur de génération:", error);
                          toast.error("Échec de la génération du fichier");
                        } finally {
                          setIsFileLoading(false);
                        }
                      }}
                      className="btn btn-primary"
                      type="button"
                    >
                      Génerer
                    </button>
                  )}
                </div>
              </div>
            </Scrollbars>
          </div>
        </div>
      </div>
      {/* Sidebar Droit */}
    </>
  );
};

export default FicheNote;
